import { useState } from 'react'
import { Link, useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { gql } from 'graphql-request'
import { ChevronDown } from 'lucide-react'

import { useCurrentUser } from '@contexts/currentUser'
import { useQuery } from '@hooks/graphql'

import Breadcrumbs from '@components/Breadcrumbs'
import Spinner from '@components/Spinner'
import { Button } from '@/components/ui/button'
import Pagination from '@components/Pagination'
import PersonalTutorCard from '@/educators/components/TutorCard'
import CommunityTutorCard from '@/educators/components/CommunityTutorCard'
import Image from '@components/Image'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@components/ui/table'
import { stripMarkdown } from '@helpers/format'
import { truncate } from 'lodash'
import { Badge } from '@components/ui/badge'
import { getTutorVisualStyle } from '@helpers/courseUtils'

import SearchInput from '@/components/SearchInput'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

import NewTutorDialog from './NewTutorDialog'
import NoResults from './NoResults'
import EmptyList from './EmptyList'
import Tag from './Tag'

const TAGS_QUERY = gql`
  query tutorTags {
    tutorTags {
      id
      slug
      name
      color
    }
  }
`

const TUTORS_QUERY = gql`
  query tutors($audience: AudienceEnum!, $search: String, $tags: [String!], $page: Int) {
    tutors(audience: $audience, search: $search, tags: $tags, page: $page, perPage: 9) {
      nodes {
        id
        name
        createdAt
        description
        summary
        visibility
        organizationName
        userName
        image {
          url
        }
        userName
        socratic
        stemModeEnabled
        copiesCount
        knowledgeBaseEnabled
        suggestionsEnabled
      }
      pagesCount
      nodesCount
    }
  }
`

const TutorList = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const audience = searchParams.get('audience') || 'me'
  const page = searchParams.get('page') || 1
  const search = searchParams.get('search') || ''
  const tags = searchParams.getAll('tags') || []
  const filtersApplied = (audience && audience !== 'me' && audience !== 'organization') || (page && page !== 1) || search || tags.length > 0
  const [searchTerm, setSearchTerm] = useState(search)
  const [newTutorDialogOpen, setNewTutorDialogOpen] = useState(searchParams.get('new') === 'true')

  const { currentMembership: { organization: { name: organizationName } } } = useCurrentUser()

  const { isLoading, data: { tutors: { nodes: tutors = [], nodesCount, pagesCount } = {} } = {} } = useQuery({
    queryKey: ['tutors', page, audience, tags, search],
    gqlQuery: TUTORS_QUERY,
    variables: { page: parseInt(page), audience: audience.toUpperCase(), tags, search }
  })

  const { data: { tutorTags: allTags = [] } = {} } = useQuery({
    queryKey: ['tutorTags'],
    gqlQuery: TAGS_QUERY
  })

  const updateParams = newParams => {
    const params = {
      audience,
      page,
      search,
      tags,
      ...newParams
    }

    if (params.page === 1) delete params.page
    if (params.search === '') delete params.search
    if (params.audience === 'me') delete params.audience
    if (params.tags.length === 0) delete params.tags

    setSearchParams(params)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSearchParams({})
  }

  const clearSearch = () => {
    setSearchTerm('')
    updateParams({ search: '', page: 1 })
  }

  const TutorCard = audience === 'community' ? CommunityTutorCard : PersonalTutorCard
  const isMyTutorsView = audience === 'me'

  return (
    <>
      <Breadcrumbs>
        <Breadcrumbs.Text label='Tutors' />
      </Breadcrumbs>

      <h1 className='text-2xl font-bold mx-5 my-4'>Tutors</h1>

      <div className='h-[calc(100%-64px)] flex flex-col px-5'>
        <div className='flex flex-wrap gap-3 items-center justify-start'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className='w-full sm:w-[200px] justify-between h-10 flex-shrink-0'>
                {audience === 'me' && 'My tutors'}
                {audience === 'organization' && (organizationName ? `${organizationName}'s tutors` : 'Your organization\'s tutors')}
                {audience === 'community' && 'Community tutors'}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[200px]" align="start">
              <DropdownMenuItem onClick={() => updateParams({ audience: 'me', page: 1 })}>
                My tutors
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => updateParams({ audience: 'organization', page: 1 })}>
                {organizationName ? `${organizationName}'s tutors` : 'Your organization\'s tutors'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => updateParams({ audience: 'community', page: 1 })}>
                Community tutors
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <SearchInput
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            onSearch={() => updateParams({ search: searchTerm, page: 1 })}
            onClear={clearSearch}
            placeholder="Search tutors..."
            className="w-full sm:flex-1 sm:min-w-[200px] sm:max-w-[384px]"
          />

          <div className='relative w-full mt-3 sm:mt-0 sm:w-auto flex-shrink-0 sm:ml-auto'>
            <NewTutorDialog open={newTutorDialogOpen} onOpenChange={setNewTutorDialogOpen} />
          </div>
        </div>

        <div className='flex relative items-center mb-4'>
          <div className='absolute top-0 right-0 h-full z-10 w-[100px] sm:mr-[100px] mr-0 pointer-events-none' />
          <div className='relative z-0 sm:px-0 px-5 flex mt-3 gap-3 w-full overflow-x-auto'>
            <For each='tag' of={allTags}>
              <Tag
                selected={tags.includes(tag.slug)}
                key={tag.slug}
                onClick={() => {
                  const newTags = tags.includes(tag.slug)
                    ? tags.filter(t => t !== tag.slug)
                    : [...tags, tag.slug]
                  updateParams({ tags: newTags, page: 1 })
                }}
                color={tag.color}
                name={tag.name}
              />
            </For>
          </div>
        </div>

        <Choose>
          <When condition={isLoading}>
            <Spinner className='flex items-center justify-center w-full h-full' />
          </When>

          <When condition={filtersApplied && nodesCount === 0}>
            <NoResults clearFilters={clearFilters} />
          </When>

          <When condition={!filtersApplied && nodesCount === 0 && !isMyTutorsView}>
            <EmptyList />
          </When>

          <When condition={!filtersApplied && nodesCount === 0 && isMyTutorsView}>
            <div className="text-center py-10">
              <h2 className="text-xl font-semibold">You don't have any tutors yet.</h2>
              <p className="text-gray-500">Go ahead and create one to get started!</p>
            </div>
          </When>

          <Otherwise>
            <Choose>
              <When condition={isMyTutorsView}>
                <div className='overflow-x-auto p-[1px]'>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]"></TableHead>
                        <TableHead className='min-w-[200px]'>Name</TableHead>
                        <TableHead className='min-w-[250px]'>Description</TableHead>
                        <TableHead className='min-w-[350px]'>Features</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <For each='tutor' of={tutors}>
                        <TableRow
                          key={tutor.id}
                          className='cursor-pointer hover:bg-muted'
                          onClick={() => navigate(`/educators/tutors/${tutor.id}`, { state: { previousListParams: location.search } })}
                        >
                          <TableCell>
                            <div className='m-1'>
                              <Link
                                to={`/educators/tutors/${tutor.id}`}
                                state={{ previousListParams: location.search }}
                                onClick={e => e.stopPropagation()}
                              >
                                <Choose>
                                  <When condition={tutor.image?.url}>
                                    <Image
                                      className='object-cover w-10 h-10 rounded-md'
                                      src={tutor.image.url}
                                      placeholder={<div className='w-10 h-10 rounded-md bg-gray-200 animate-pulse' />}
                                    />
                                  </When>
                                  <Otherwise>
                                    {(() => {
                                      const { emoji, gradientFrom, gradientTo } = getTutorVisualStyle(tutor.id);
                                      return (
                                        <div
                                          className='w-10 h-10 rounded-md flex items-center justify-center'
                                          style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
                                        >
                                          <span className='text-xl'>{emoji}</span>
                                        </div>
                                      );
                                    })()}
                                  </Otherwise>
                                </Choose>
                              </Link>
                            </div>
                          </TableCell>
                          <TableCell>
                            {tutor.name || 'Untitled Tutor'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {truncate(stripMarkdown(tutor.summary || tutor.description || ''), { length: 70 })}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1 items-center">
                              <If condition={tutor.socratic}>
                                <Badge variant='secondary' className='rounded-[4px] font-medium font-dm-mono'>
                                  <span className='mr-1'>🧠</span> Socratic
                                </Badge>
                              </If>
                              <If condition={tutor.stemModeEnabled}>
                                <Badge variant='secondary' className='rounded-[4px] font-medium font-dm-mono'>
                                  <span className='mr-1'>🧪</span> STEM Mode
                                </Badge>
                              </If>
                              <If condition={tutor.knowledgeBaseEnabled}>
                                <Badge variant='secondary' className='rounded-[4px] font-medium font-dm-mono'>
                                  <span className='mr-1'>📚</span> Knowledge Base
                                </Badge>
                              </If>
                              <If condition={tutor.suggestionsEnabled}>
                                <Badge variant='secondary' className='rounded-[4px] font-medium font-dm-mono'>
                                  <span className='mr-1'>💬</span> Suggestions
                                </Badge>
                              </If>
                            </div>
                          </TableCell>
                        </TableRow>
                      </For>
                    </TableBody>
                  </Table>
                </div>
              </When>
              <Otherwise>
                <div className='grid xl:grid-cols-3 grid-cols-1 gap-2 md:gap-4'>
                  <For each='tutor' of={tutors}>
                    <Link
                      key={tutor.id}
                      to={`/educators/tutors/${tutor.id}`}
                      state={{ previousListParams: location.search }}
                    >
                      <TutorCard
                        tutorId={tutor.id}
                        name={tutor.name}
                        description={tutor.summary || tutor.description}
                        imageUrl={tutor.image?.url}
                        userName={tutor.userName}
                        socratic={tutor.socratic}
                        stemModeEnabled={tutor.stemModeEnabled}
                        copiesCount={tutor.copiesCount}
                        knowledgeBaseEnabled={tutor.knowledgeBaseEnabled}
                        suggestionsEnabled={tutor.suggestionsEnabled}
                      />
                    </Link>
                  </For>
                </div>
              </Otherwise>
            </Choose>

            <Pagination page={parseInt(page)} pagesCount={pagesCount} setPage={newPage => updateParams({ page: newPage })} />
          </Otherwise>
        </Choose>
      </div>
    </>
  )
}

export default TutorList
